
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { Step1BasicInfo } from "./meal-form/Step1BasicInfo";
import { Step2DeliveryInfo } from "./meal-form/Step2DeliveryInfo";
import { Step3Details } from "./meal-form/Step3Details";
import { StepReview } from "./meal-form/StepReview";
import { StepperDots } from "./meal-form/StepperDots";
import { X } from "lucide-react";

interface TimeSlot {
  dateRange: {
    from: Date;
    to?: Date;
  };
  startTime: string;
  endTime: string;
  availablePortions: number;
}

interface CreateMealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (meal: any) => void;
  initialData?: any;
}

export const CreateMealDialog = ({
  open,
  onOpenChange,
  onSubmit,
  initialData,
}: CreateMealDialogProps) => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    price: initialData?.price?.toString() || "",
    description: initialData?.description || "",
    images: initialData?.images || [],
    availabilitySlots: initialData?.availability_slots || [],
    deliveryAvailable: initialData?.delivery_available || false,
    deliveryFee: initialData?.delivery_fee?.toString() || "",
    cuisine_type: initialData?.cuisine_type || "",
    meal_type: initialData?.meal_type || "",
    dietary_tags: initialData?.dietary_tags || [],
    ingredients: initialData?.ingredients || [],
    allergens: initialData?.allergens || [],
    calories: initialData?.calories?.toString() || "",
    carbs: initialData?.carbs?.toString() || "",
    protein: initialData?.protein?.toString() || "",
    fat: initialData?.fat?.toString() || "",
    hasLimitedPortions: initialData?.has_limited_portions || false,
    available_portions: initialData?.available_portions?.toString() || "",
    preparation_type: initialData?.preparation_type || "scheduled",
    prep_time_hours: initialData?.prep_time_hours?.toString() || "",
    cutoff_time: initialData?.cutoff_time || "",
    max_daily_orders: initialData?.max_daily_orders?.toString() || "",
    pickup_window_start: initialData?.pickup_window_start || "",
    pickup_window_end: initialData?.pickup_window_end || "",
    available_days: initialData?.available_days || [],
    pickup_instructions: initialData?.pickup_instructions || "",
    portion_size_value: initialData?.portion_size_value?.toString() || "",
    portion_size_unit: initialData?.portion_size_unit || "servings",
    use_custom_pickup_address: initialData?.use_custom_pickup_address || false,
    pickup_address: initialData?.pickup_address || "",
    pickup_city: initialData?.pickup_city || "",
    pickup_state: initialData?.pickup_state || "",
    pickup_postal_code: initialData?.pickup_postal_code || "",
  });

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    if (!formData.name || !formData.price || !formData.description || formData.images.length === 0 || !formData.pickup_instructions) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields and upload at least one image",
        variant: "destructive",
      });
      return;
    }

    // Validate required fields for custom address if toggled on
    if (formData.use_custom_pickup_address) {
      if (!formData.pickup_address || !formData.pickup_city || !formData.pickup_state || !formData.pickup_postal_code) {
        toast({
          title: "Missing Information",
          description: "Please fill in all custom address fields",
          variant: "destructive",
        });
        return;
      }
    }

    const mainImage = formData.images[0] || null;

    // Convert form data to database format
    const submissionData = {
      ...formData,
      id: initialData?.id,
      price: parseFloat(formData.price),
      image: mainImage,
      images: formData.images,
      delivery_fee: formData.deliveryFee ? parseFloat(formData.deliveryFee) : null,
      delivery_available: formData.deliveryAvailable,
      calories: parseInt(formData.calories) || null,
      carbs: parseFloat(formData.carbs) || null,
      protein: parseFloat(formData.protein) || null,
      fat: parseFloat(formData.fat) || null,
      meal_type: formData.meal_type,
      has_limited_portions: formData.hasLimitedPortions,
      available_portions: formData.hasLimitedPortions && formData.available_portions ? parseInt(formData.available_portions) : null,
      preparation_type: formData.preparation_type,
      // On-demand specific fields
      prep_time_hours: formData.preparation_type === "on_demand" ? parseInt(formData.prep_time_hours) || null : null,
      cutoff_time: formData.preparation_type === "on_demand" ? formData.cutoff_time : null,
      max_daily_orders: formData.preparation_type === "on_demand" ? parseInt(formData.max_daily_orders) || null : null,
      pickup_window_start: formData.preparation_type === "on_demand" ? formData.pickup_window_start : null,
      pickup_window_end: formData.preparation_type === "on_demand" ? formData.pickup_window_end : null,
      available_days: formData.preparation_type === "on_demand" ? formData.available_days : [],
      pickup_instructions: formData.pickup_instructions || null,
      // Scheduled specific fields
      availability_slots: formData.preparation_type === "scheduled" ? formData.availabilitySlots : [],
      // Portion size fields
      portion_size_value: formData.portion_size_value ? parseFloat(formData.portion_size_value) : null,
      portion_size_unit: formData.portion_size_unit || null,
      // Custom pickup address fields
      use_custom_pickup_address: formData.use_custom_pickup_address,
      pickup_address: formData.use_custom_pickup_address ? formData.pickup_address : null,
      pickup_city: formData.use_custom_pickup_address ? formData.pickup_city : null,
      pickup_state: formData.use_custom_pickup_address ? formData.pickup_state : null,
      pickup_postal_code: formData.use_custom_pickup_address ? formData.pickup_postal_code : null,
    };

    console.log("Submitting meal data:", submissionData);
    onSubmit(submissionData);

    if (!initialData) {
      setFormData({
        name: "",
        price: "",
        description: "",
        images: [],
        availabilitySlots: [],
        deliveryAvailable: false,
        deliveryFee: "",
        cuisine_type: "",
        meal_type: "",
        dietary_tags: [],
        ingredients: [],
        allergens: [],
        calories: "",
        carbs: "",
        protein: "",
        fat: "",
        hasLimitedPortions: false,
        available_portions: "",
        preparation_type: "scheduled",
        prep_time_hours: "",
        cutoff_time: "",
        max_daily_orders: "",
        pickup_window_start: "",
        pickup_window_end: "",
        available_days: [],
        pickup_instructions: "",
        portion_size_value: "",
        portion_size_unit: "servings",
        use_custom_pickup_address: false,
        pickup_address: "",
        pickup_city: "",
        pickup_state: "",
        pickup_postal_code: "",
      });
    }
    setCurrentStep(1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1BasicInfo
            formData={formData}
            onChange={handleFieldChange}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <Step2DeliveryInfo
            formData={{
              availabilitySlots: formData.availabilitySlots,
              hasLimitedPortions: formData.hasLimitedPortions,
              available_portions: formData.available_portions,
              deliveryAvailable: formData.deliveryAvailable,
              deliveryFee: formData.deliveryFee,
              pickup_instructions: formData.pickup_instructions,
              preparation_type: formData.preparation_type,
              prep_time_hours: formData.prep_time_hours,
              cutoff_time: formData.cutoff_time,
              max_daily_orders: formData.max_daily_orders,
              pickup_window_start: formData.pickup_window_start,
              pickup_window_end: formData.pickup_window_end,
              available_days: formData.available_days,
              use_custom_pickup_address: formData.use_custom_pickup_address,
              pickup_address: formData.pickup_address,
              pickup_city: formData.pickup_city,
              pickup_state: formData.pickup_state,
              pickup_postal_code: formData.pickup_postal_code,
            }}
            onChange={handleFieldChange}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <Step3Details
            formData={{
              cuisine_type: formData.cuisine_type,
              meal_type: formData.meal_type,
              dietary_tags: formData.dietary_tags,
              allergens: formData.allergens,
              ingredients: formData.ingredients,
              calories: formData.calories,
              carbs: formData.carbs,
              protein: formData.protein,
              fat: formData.fat,
              portion_size_value: formData.portion_size_value,
              portion_size_unit: formData.portion_size_unit,
            }}
            onChange={handleFieldChange}
            onReview={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <StepReview
            formData={formData}
            onSubmit={handleSubmit}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] sm:h-auto overflow-y-auto rounded-2xl p-6 pt-10 mx-auto w-[calc(100%-2rem)]" autoFocus={false}>
        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 p-2 rounded-lg text-gray-500 hover:text-gray-900 focus:outline-none"
          tabIndex={-1}
        >
          <X className="h-4 w-4" />
        </button>
        <DialogHeader>
          <DialogTitle className="text-center text-base text-black">
            {initialData ? "Edit Meal" : "Create New Meal"}
          </DialogTitle>
        </DialogHeader>
        <StepperDots currentStep={currentStep} totalSteps={4} />
        <div className="mt-6">
          {renderStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
